import { useEffect, useState, type ComponentType } from 'react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import type { Card as SupCard, TeamOpt, SupplementBoardCard as SupplementBoardCardType } from '../pages/Supplements';

/**
 * LA TARJETA DE SU VISTA, DENTRO DEL TO DO.
 *
 * Al desplegar una fila del To Do se ve la MISMA tarjeta que en el board del departamento: si el
 * claim está en "First Estimate", se abre igual que abriéndolo desde la vista First Estimate.
 * No es una copia — se reusa el componente del board (SupplementBoardCard) y los datos salen del
 * mismo SQL, así que lo que cambie en el board cambia acá.
 *
 * Se carga SOLO al desplegar (la fila colapsada no pide nada) y solo para los departamentos que
 * tienen tarjeta rica; en el resto no estorba: no pinta nada y la fila queda como siempre.
 *
 * EL BOARD DE SUPPLEMENTS SE BAJA BAJO DEMANDA (auditoría 2026-09-15): este archivo importaba
 * `pages/Supplements` de forma ESTÁTICA, y como el To Do es eager, arrastraba al chunk de arranque
 * las 3.600 líneas del board con negociación, medidas de techo, calendario, chat, correo y drawer
 * (Vite lo avisaba en cada build: "dynamic import will not move module"). Ahora el módulo se pide
 * con import() al desplegar la primera fila; el tipo sigue siendo el del board.
 */
type ModuloSupplements = Pick<typeof import('../pages/Supplements'), 'SupplementBoardCard' | 'supplementViewKey'>;
let moduloSupplements: Promise<ModuloSupplements> | null = null;
const cargarSupplements = () => (moduloSupplements ??= import('../pages/Supplements').then((m) => ({
  SupplementBoardCard: m.SupplementBoardCard,
  supplementViewKey: m.supplementViewKey,
})));

export function BoardCardBridge({
  claimId, deptKey, deptStatus, onChanged,
}: {
  claimId: string;
  /** Departamento del To Do (supplements, claims, payments…). */
  deptKey: string;
  /** Status del claim EN ESE depto = la vista del board de la que sale la tarjeta. */
  deptStatus: string | null | undefined;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [card, setCard] = useState<SupCard | null>(null);
  const [teams, setTeams] = useState<TeamOpt[]>([]);
  const [mod, setMod] = useState<ModuloSupplements | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none'>('loading');

  const supported = deptKey === 'supplements';

  useEffect(() => {
    if (!supported) { setState('none'); return; }
    let alive = true;
    setState('loading');
    Promise.all([
      api.get<{ card: SupCard | null }>(`/api/supplements/cards/${claimId}`),
      api.get<{ teams: TeamOpt[] }>('/api/teams/options?department=supplements').catch(() => ({ teams: [] as TeamOpt[] })),
      cargarSupplements(),
    ])
      .then(([c, tm, m]) => {
        if (!alive) return;
        setMod(m);
        setTeams(tm.teams || []);
        setCard(c.card);
        setState(c.card ? 'ready' : 'none');
      })
      .catch(() => { if (alive) setState('none'); });
    return () => { alive = false; };
  }, [claimId, supported]);

  if (!supported || state === 'none') return null;
  if (state === 'loading' || !mod) {
    return <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: '10px 0' }}>{t('common.loading')}</div>;
  }
  if (!card) return null;

  const Tarjeta: ComponentType<Parameters<typeof SupplementBoardCardType>[0]> = mod.SupplementBoardCard;
  // La vista sale del status del depto; si no matchea ninguna columna, cae a la tarjeta simple.
  const viewKey = mod.supplementViewKey(deptStatus ?? card.status);
  return (
    <div style={{ margin: '10px 0 4px' }}>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 6 }}>
        {t('todo.asInView', { view: deptStatus ?? card.status ?? '—' })}
      </div>
      <Tarjeta
        viewKey={viewKey}
        card={card}
        ctx={{ teams, onChanged, flash: () => {}, defaultOpen: true }}
      />
    </div>
  );
}
